import { Box, Typography, Button, Card, CardContent, TextField } from "@mui/material";
import { orange } from "@mui/material/colors";
import { useScopeOfWork } from "../../context/ScopeOfWorkContext";
import { Category } from "../../context/scope-of-work.types";
import { useCategoriesQuery } from "./hooks/useCategoriesQuery";
import { categoryUIConfig } from "./config/CategoryUiConfig";
import { QueryState } from "../../../../query-states/GlobalQueryState";

interface CategoryData {
  uid: string;
  name: "Commercial" | "Residential";
  imageUrl?: string;
}

interface CategoriesProps {
  selected: Category | null;
  lockCategory?: boolean;
}

export const Categories = ({ selected, lockCategory = false }: CategoriesProps) => {
  const { dispatch } = useScopeOfWork();

  const { data: categories, isLoading, isError } = useCategoriesQuery();
  const safeCategories = Array.isArray(categories) ? categories : (categories?.items ?? []);

  const handleSelectCategory = (category: CategoryData) => {
    if (lockCategory) return;

    dispatch({
      type: "SET_CATEGORY",
      payload: category,
    });
  };

  if (isLoading || isError) {
    return <QueryState isLoading={isLoading} isError={isError} serviceName="categories" />;
  }

  return (
    <Box>
      <Box mr={2} display="flex" justifyContent="center" alignItems="center" gap={4} flexWrap="wrap">
        {safeCategories?.map((cat: CategoryData) => {
          const config = categoryUIConfig[cat.name];
          if (!config) return null;

          const isSelected = selected?.uid === cat.uid;
          const imageUrl = cat.imageUrl;

          return (
            <Card
              key={cat.uid}
              onClick={() => handleSelectCategory(cat)}
              sx={{
                flex: "1",
                borderRadius: 4,
                boxShadow: isSelected ? `0 0 0 3px ${config.color}60` : "0 2px 8px rgba(0,0,0,0.05)",
                border: `1px solid ${config.color}${isSelected ? "99" : "30"}`,
                transform: isSelected ? "translateY(-2px)" : "none",
                transition: "all 0.25s ease",
                cursor: lockCategory ? "not-allowed" : "pointer",
                opacity: lockCategory && !isSelected ? 0.45 : 1,
                maxWidth: 360,
              }}
            >
              <CardContent sx={{ textAlign: "left", p: 4 }}>
                <Box
                  sx={{
                    width: 160,
                    height: 160,
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: config.color,
                    margin: "0 auto",
                    mb: 2,
                    overflow: "hidden",
                  }}
                >
                  {imageUrl ? (
                    <Box
                      component="img"
                      src={imageUrl}
                      alt={cat.name}
                      sx={{
                        width: 180,
                        height: 180,
                        objectFit: "contain",
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: 120,
                        height: 120,
                        borderRadius: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: config.color,
                      }}
                    >
                      {config.icon}
                    </Box>
                  )}
                </Box>

                <Typography variant="h6" fontWeight={700}>
                  {cat.name}
                </Typography>

                <Typography variant="subtitle2" fontWeight={600} sx={{ color: orange[500], mb: 1 }}>
                  {config.subtitle}
                </Typography>

                <Typography variant="body2" sx={{ color: "text.secondary", mb: 3, lineHeight: 1.6 }}>
                  {config.description}
                </Typography>

                <Button
                  fullWidth
                  disabled={lockCategory}
                  variant={isSelected ? "contained" : "text"}
                  sx={{
                    textTransform: "none",
                    backgroundColor: isSelected ? `${config.color}25` : `${config.color}10`,
                    color: "var(--fx-surface-text, #0F172A)",
                    borderRadius: 1,
                    px: 3,
                    py: 1,
                    "&:hover": {
                      backgroundColor: orange[500],
                      color: "white",
                      boxShadow: "none",
                    },
                    "&:focus": {
                      outline: "none",
                      boxShadow: "none",
                    },
                    "&:focus-visible": {
                      outline: "none",
                      boxShadow: "none",
                    },
                    "&.Mui-disabled": {
                      backgroundColor: isSelected ? `${config.color}25` : `${config.color}10`,
                      color: isSelected ? "black" : "text.disabled",
                      opacity: isSelected ? 1 : 0.6,
                    },
                  }}
                >
                  {lockCategory ? (isSelected ? "Selected" : "Locked") : isSelected ? "Selected" : `Select ${cat.name}`}
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </Box>
    </Box>
  );
};
